import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { fecha } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const MONTHS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const iso = (y, m, d) => new Date(Date.UTC(y, m, d)).toISOString().slice(0, 10);
export const addDays = (s, n) => {
  const [y, m, d] = s.split("-").map(Number);
  return iso(y, m - 1, d + n);
};
// Misma regla que el backend: noches [inicio, fin); un alquiler de un día ocupa ese día.
export const covers = (item, day) => (item.start === item.end ? day === item.start : item.start <= day && day < item.end);
const STATUS_CLS = {
  pending: "bg-acento text-white",
  active: "bg-marca text-white",
  completed: "bg-[#8A8A8A] text-white",
  pending_payment: "bg-[#E89B33] text-white",
};

export default function Calendar() {
  const navigate = useNavigate();
  const today = new Date();
  const [ym, setYm] = useState({ y: today.getFullYear(), m: today.getMonth() });
  const [data, setData] = useState({ vehicles: [], items: [] });
  const [blockForm, setBlockForm] = useState(null);
  const [blockOpen, setBlockOpen] = useState(null);

  const days = useMemo(() => {
    const n = new Date(Date.UTC(ym.y, ym.m + 1, 0)).getUTCDate();
    return Array.from({ length: n }, (_, i) => iso(ym.y, ym.m, i + 1));
  }, [ym]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/fleet/calendar", { params: { start: days[0], end: addDays(days[days.length - 1], 1) } });
      setData(data);
    } catch (err) {
      toast.error(formatError(err));
    }
  }, [days]);
  useEffect(() => { load(); }, [load]);

  const move = (delta) => setYm(({ y, m }) => {
    const d = new Date(Date.UTC(y, m + delta, 1));
    return { y: d.getUTCFullYear(), m: d.getUTCMonth() };
  });

  const itemAt = (vid, day) => data.items.find((it) => it.vehicle_id === vid && covers(it, day));

  const clickCell = (v, day) => {
    const it = itemAt(v.id, day);
    if (!it) return setBlockForm({ vehicle_id: v.id, vehicle_name: v.name, start: day, end: addDays(day, 1), reason: "" });
    if (it.kind === "reservation") return navigate(`/admin/reservations/${it.id}`);
    return setBlockOpen(it);
  };

  const saveBlock = async () => {
    try {
      await api.post("/fleet/blocks", { vehicle_id: blockForm.vehicle_id, start: blockForm.start, end: blockForm.end, reason: blockForm.reason });
      toast.success("Bloqueo creado");
      setBlockForm(null);
      load();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const deleteBlock = async () => {
    try {
      await api.delete(`/fleet/blocks/${blockOpen.id}`);
      setBlockOpen(null);
      load();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <header className="bg-white border-b border-[#DCD8D0]">
        <div className="container-x py-4 flex items-center justify-between">
          <Link to="/admin" className="inline-flex items-center text-[#5C5C5C] hover:text-marca">
            <ArrowLeft className="h-4 w-4 mr-2" /> Panel
          </Link>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => move(-1)} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
            <span className="font-display font-semibold w-40 text-center" data-testid="cal-month">{MONTHS[ym.m]} {ym.y}</span>
            <Button variant="ghost" onClick={() => move(1)} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      </header>

      <main className="container-x py-8">
        <div className="flex flex-wrap gap-4 text-xs text-[#5C5C5C] mb-4">
          <span><span className="inline-block w-3 h-3 rounded bg-[#E89B33] mr-1" />Pago pendiente</span>
          <span><span className="inline-block w-3 h-3 rounded bg-acento mr-1" />Reservada</span>
          <span><span className="inline-block w-3 h-3 rounded bg-marca mr-1" />En curso</span>
          <span><span className="inline-block w-3 h-3 rounded bg-[#8A8A8A] mr-1" />Terminada</span>
          <span><span className="inline-block w-3 h-3 rounded bg-[#DCD8D0] mr-1" />Bloqueo</span>
          <span>Pulsa un día libre para bloquearlo.</span>
        </div>
        <div className="bg-white border border-[#DCD8D0] rounded-xl overflow-x-auto">
          <table className="text-xs border-collapse" data-testid="cal-grid">
            <thead>
              <tr>
                <th className="sticky left-0 bg-white text-left p-2 min-w-[160px] border-b border-[#EAE7E0]">Vehículo</th>
                {days.map((d) => (
                  <th key={d} className="p-1 w-8 text-center font-normal text-[#8A8A8A] border-b border-[#EAE7E0]">{Number(d.slice(8))}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.vehicles.map((v) => (
                <tr key={v.id}>
                  <td className="sticky left-0 bg-white p-2 border-b border-[#EAE7E0]">
                    <div className="font-medium text-[#1A1A1A]">{v.name}</div>
                    <div className="font-mono text-[10px] text-[#8A8A8A]">{v.plate}</div>
                  </td>
                  {days.map((d) => {
                    const it = itemAt(v.id, d);
                    const cls = !it ? "hover:bg-[#F7F5F0]" : it.kind === "block" ? "bg-[#DCD8D0]" : STATUS_CLS[it.status] || STATUS_CLS.pending;
                    const first = it && (it.start === d || d === days[0]);
                    return (
                      <td key={d} onClick={() => clickCell(v, d)} title={it ? `${it.label}${it.code ? ` · ${it.code}` : ""}` : "Libre"}
                        className={`h-9 w-8 border border-[#F2F0EB] cursor-pointer align-middle ${cls}`}
                        data-testid={`cal-${v.plate}-${d}`}>
                        {first && <span className="block truncate w-8 px-0.5">{it.code || it.label}</span>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>

      <Dialog open={!!blockForm} onOpenChange={(open) => !open && setBlockForm(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Bloquear {blockForm?.vehicle_name}</DialogTitle></DialogHeader>
          {blockForm && (
            <div className="space-y-3" data-testid="block-form">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Desde</Label><Input type="date" value={blockForm.start} onChange={(e) => setBlockForm((f) => ({ ...f, start: e.target.value }))} /></div>
                <div><Label>Hasta</Label><Input type="date" value={blockForm.end} onChange={(e) => setBlockForm((f) => ({ ...f, end: e.target.value }))} /></div>
              </div>
              <p className="text-xs text-[#8A8A8A]" data-testid="block-hint">
                El día «hasta» ya queda libre para una recogida. Para bloquear un solo día, pon la misma fecha en las dos casillas.
              </p>
              <div><Label>Motivo</Label><Input placeholder="Taller, ITV, uso propio…" value={blockForm.reason} onChange={(e) => setBlockForm((f) => ({ ...f, reason: e.target.value }))} data-testid="block-reason" /></div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setBlockForm(null)}>Cancelar</Button>
                <Button onClick={saveBlock} className="bg-marca hover:bg-marca-hover text-white" data-testid="block-save">Bloquear</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!blockOpen} onOpenChange={(open) => !open && setBlockOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Bloqueo</DialogTitle></DialogHeader>
          {blockOpen && (
            <div className="space-y-3">
              <p className="text-sm text-[#5C5C5C]">{blockOpen.label} · {fecha(blockOpen.start)} → {fecha(blockOpen.end)}</p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setBlockOpen(null)}>Cerrar</Button>
                <Button variant="destructive" onClick={deleteBlock} data-testid="block-delete">Quitar bloqueo</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
